import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';

/**
 * Seccion Compras & Proveedores: gasto en compras con delta, pendientes de
 * recibir, ranking de proveedores del periodo, y comparador de precios entre
 * proveedores (mismo producto, distinto costo).
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

function DeltaChip({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? 'text-muted-foreground bg-muted' : good ? 'text-green-600 bg-green-500/10' : 'text-red-600 bg-red-500/10';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

export default function ComprasSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/compras', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando compras'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando compras...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Compras del período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.total.value)}</p>
          <DeltaChip value={d.kpis.total.deltaPct} invert />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Órdenes de compra</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.cantidad.value}</p>
          <span className="text-xs text-muted-foreground">en el período</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Pendientes de recibir</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${d.kpis.pendientes.value > 0 ? 'text-amber-600' : ''}`}>{d.kpis.pendientes.value}</p>
          <span className="text-xs text-muted-foreground">{d.kpis.pendientes.monto > 0 ? `${money(d.kpis.pendientes.monto)} en camino` : 'todo recibido'}</span>
        </CardContent></Card>
      </div>

      {!d.comprasCargadas && (
        <p className="text-xs text-amber-600 px-1">
          ⚠ Todavía casi no se cargan compras en el sistema. Se cargan desde <span className="underline font-medium">Inventario → Compras</span> — al recibirlas alimentan egresos, resultado y esta sección automáticamente.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Compras por mes</h3>
            <p className="text-xs text-muted-foreground mb-3">Histórico de gasto en proveedores</p>
            <ChartContainer
              config={{ total: { label: 'Comprado', color: 'hsl(var(--chart-5))' } }}
              className="h-[210px] w-full"
            >
              <AreaChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <defs>
                  <AreaGradient id="fillCompras" color="var(--color-total)" />
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
                <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ total: 'Comprado' }, money)} />} />
                <Area type="monotone" dataKey="total" stroke="var(--color-total)" strokeWidth={2.2} fill="url(#fillCompras)" dot={{ r: 3 }} />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Proveedores del período</h3>
            {d.porProveedor.length === 0 && <p className="text-sm text-muted-foreground">Sin compras en el período</p>}
            <div className="space-y-2">
              {d.porProveedor.map((p: any) => {
                const max = Math.max(1, ...d.porProveedor.map((x: any) => x.total));
                return (
                  <div key={p.proveedor}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span className="truncate max-w-[220px]">{p.proveedor} <span className="text-xs text-muted-foreground">({p.compras})</span></span>
                      <span className="font-medium">{money(p.total)}</span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(p.total / max) * 100}%`, background: '#D85A30' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="font-semibold text-sm">Comparador de precios entre proveedores</h3>
            <span className="text-xs text-muted-foreground">mismo producto, distinto costo — ordenado por ahorro posible</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Datos del módulo Productos (precios por proveedor). Comprarle al de la izquierda ahorra el % de la derecha.</p>
          {d.comparador.length === 0 && <p className="text-sm text-muted-foreground">No hay productos con 2+ proveedores activos cargados.</p>}
          {d.comparador.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Producto</th>
                    <th className="pb-1.5 font-normal">Mejor proveedor</th>
                    <th className="pb-1.5 font-normal text-right">Mejor costo</th>
                    <th className="pb-1.5 font-normal text-right">Peor costo</th>
                    <th className="pb-1.5 font-normal text-right">Ahorro</th>
                  </tr>
                </thead>
                <tbody>
                  {d.comparador.map((c: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2 truncate max-w-[260px]">{c.producto}</td>
                      <td className="py-1.5 pr-2 truncate max-w-[140px] text-muted-foreground">{c.mejorProveedor}</td>
                      <td className="py-1.5 pr-2 text-right font-medium text-green-600">{money(c.mejor)}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{money(c.peor)}</td>
                      <td className={`py-1.5 text-right font-medium ${c.ahorroPct >= 15 ? 'text-green-600' : 'text-amber-600'}`}>{c.ahorroPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
